import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, Users, BrainCircuit, TrendingUp, ShieldAlert, ChevronRight,
  Activity, ChevronDown, Check, Zap, Clock, Sparkles, DollarSign, Sliders,
  ShieldCheck, AlertTriangle, X, Building, Award, Mail, Phone, Send, FileText
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import NousLogo from './NousLogo';
import GlobalFooter from './GlobalFooter';
import heroCoach from '../assets/hero_coach.png';
import mentorDashboardMockup from '../assets/mentor_dashboard_mockup.jpg';
import mentorActionTrack from '../assets/mentor_action_track.jpg';

export default function CoachLandingPage() {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [athletesCount, setAthletesCount] = useState(15);
  const [extraFee, setExtraFee] = useState(10);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [demoSubmitted, setDemoSubmitted] = useState(false);
  const [demoForm, setDemoForm] = useState({
    name: '',
    email: '',
    clubName: '',
    phone: '',
    athletesNum: '20-50'
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const monthlyExtraRevenue = athletesCount * extraFee;
  const netMonthlyProfit = monthlyExtraRevenue - 29;

  const handleDemoSubmit = (e) => {
    e.preventDefault();
    setDemoSubmitted(true);
    setTimeout(() => {
      // simulate receipt
    }, 500);
  };

  return (
    <div className="theme-mentor" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#050810', color: 'white', fontFamily: 'Outfit, -apple-system, BlinkMacSystemFont, sans-serif', overflowX: 'hidden' }}>
      <Helmet>
        <title>NousMentor — Software para Entrenadores de Ciclismo y Running con IA</title>
        <meta name="description" content="Supervisa a decenas de atletas en un solo panel. Alertas predictivas de sobreentrenamiento, cruce de HRV y TSB e informes semanales con IA listos en 1 clic. Piloto gratis." />
      </Helmet>

      {/* ═══════════════════════════════════════════════ */}
      {/* 1. NAVEGACIÓN SUPERIOR FIJA                     */}
      {/* ═══════════════════════════════════════════════ */}
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: isMobile ? '0.75rem 1.25rem' : '1rem 3rem',
        zIndex: 100,
        background: 'rgba(5, 8, 16, 0.85)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderBottom: '1px solid rgba(139, 92, 246, 0.15)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
          <div onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <NousLogo size="nav" showSlogan={false} variant="mentor" />
            <span style={{
              color: '#8b5cf6',
              fontSize: '0.72rem',
              fontWeight: 800,
              background: 'rgba(139, 92, 246, 0.15)',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              padding: '0.2rem 0.6rem',
              borderRadius: '20px',
              letterSpacing: '0.05em'
            }}>
              COACH B2B
            </span>
          </div>

          {!isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', fontSize: '0.88rem', fontWeight: 500, color: 'rgba(255, 255, 255, 0.7)' }}>
              <a href="#como-funciona" className="mentor-nav-link">Flujo de Trabajo</a>
              <a href="#panel" className="mentor-nav-link">Panel Consolidado</a>
              <a href="#roi" className="mentor-nav-link">Calculadora ROI</a>
              <a href="#pricing" className="mentor-nav-link">Planes B2B</a>
              <a href="#faq" className="mentor-nav-link">FAQ</a>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'transparent',
              color: 'rgba(255, 255, 255, 0.6)',
              border: 'none',
              fontSize: '0.85rem',
              fontWeight: 600,
              cursor: 'pointer',
              display: isMobile ? 'none' : 'block',
              transition: 'color 0.2s'
            }}
            onMouseOver={e => e.currentTarget.style.color = '#fff'}
            onMouseOut={e => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)'}
          >
            ← Modo Atleta
          </button>

          <button
            onClick={() => navigate('/app?mode=register&role=trainer')}
            style={{
              background: 'linear-gradient(135deg, #8b5cf6 0%, #d946ef 100%)',
              color: 'white',
              border: 'none',
              padding: isMobile ? '0.5rem 1rem' : '0.55rem 1.25rem',
              borderRadius: '20px',
              fontSize: isMobile ? '0.8rem' : '0.85rem',
              fontWeight: 800,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 4px 15px rgba(139, 92, 246, 0.35)',
              transition: 'transform 0.2s'
            }}
            onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <span>Piloto Gratis</span>
            <ArrowRight size={14} />
          </button>
        </div>
      </nav>

      {/* ═══════════════════════════════════════════════ */}
      {/* 2. HERO B2B & TEST DE 5 SEGUNDOS                */}
      {/* ═══════════════════════════════════════════════ */}
      <section style={{
        position: 'relative',
        minHeight: '100vh',
        paddingTop: isMobile ? '6.5rem' : '8.5rem',
        paddingBottom: '4rem',
        paddingLeft: '5%',
        paddingRight: '5%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        overflow: 'hidden',
        background: `linear-gradient(to bottom, rgba(5,8,16,0.7) 0%, rgba(5,8,16,0.95) 100%), url(${heroCoach})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center top'
      }}>
        {/* Glow púrpura ambiental */}
        <div style={{
          position: 'absolute',
          top: '20%', left: '50%', transform: 'translateX(-50%)',
          width: '600px', height: '400px',
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.18) 0%, rgba(217, 70, 239, 0.08) 50%, transparent 70%)',
          filter: 'blur(90px)',
          zIndex: 0,
          pointerEvents: 'none'
        }} />

        <div style={{ position: 'relative', zIndex: 1, maxWidth: '940px', margin: '0 auto' }}>
          
          {/* Badge B2B */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(139, 92, 246, 0.15)',
            border: '1px solid rgba(139, 92, 246, 0.35)',
            padding: '6px 16px',
            borderRadius: '30px',
            fontSize: '0.8rem',
            fontWeight: 700,
            letterSpacing: '0.04em',
            color: '#c4b5fd',
            marginBottom: '1.75rem',
            backdropFilter: 'blur(10px)'
          }}>
            <Sparkles size={14} color="#d946ef" /> SOFTWARE INTELIGENTE PARA ENTRENADORES Y CLUBES
          </div>

          {/* H1 Nuclear B2B: Tiempo, Control y Salud de Atletas */}
          <h1 style={{
            fontSize: isMobile ? 'clamp(2.1rem, 8vw, 2.7rem)' : 'clamp(3rem, 5.5vw, 4.2rem)',
            fontWeight: 800,
            lineHeight: 1.08,
            letterSpacing: '-0.03em',
            margin: '0 auto 1.5rem',
            maxWidth: '900px'
          }}>
            Entrena a más atletas en la mitad de tiempo.<br />
            <span style={{
              background: 'linear-gradient(135deg, #c4b5fd 0%, #8b5cf6 50%, #d946ef 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Sin que se te escape una sola lesión.
            </span>
          </h1>

          {/* Subtítulo B2B */}
          <p style={{
            fontSize: isMobile ? '1.05rem' : '1.3rem',
            color: 'rgba(255, 255, 255, 0.75)',
            lineHeight: 1.55,
            fontWeight: 400,
            maxWidth: '750px',
            margin: '0 auto 2.25rem'
          }}>
            NousMentor centraliza la carga (TSB), el HRV y el sueño de todos tus deportistas de Strava y Garmin en un solo panel. <strong style={{ color: '#fff' }}>Alertas tempranas de sobreentrenamiento</strong> e <strong style={{ color: '#fff' }}>informes semanales con IA</strong> listos en 1 clic.
          </p>

          {/* DUAL CTA DE ALTO IMPACTO B2B */}
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem',
            marginBottom: '1.25rem'
          }}>
            <button
              onClick={() => navigate('/app?mode=register&role=trainer')}
              style={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #d946ef 100%)',
                color: '#fff',
                border: 'none',
                padding: '1.1rem 2.2rem',
                borderRadius: '35px',
                fontWeight: 800,
                fontSize: isMobile ? '1.05rem' : '1.15rem',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                boxShadow: '0 10px 30px rgba(139, 92, 246, 0.4)',
                transition: 'all 0.25s',
                width: isMobile ? '100%' : 'auto'
              }}
              onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 15px 35px rgba(139, 92, 246, 0.55)'; }}
              onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 10px 30px rgba(139, 92, 246, 0.4)'; }}
            >
              <span>Iniciar Piloto Gratis con 3 Atletas</span>
              <ArrowRight size={20} />
            </button>

            <a
              href="#panel"
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                color: 'rgba(255, 255, 255, 0.85)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                padding: '1.1rem 1.8rem',
                borderRadius: '35px',
                fontWeight: 600,
                fontSize: isMobile ? '0.95rem' : '1.05rem',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.2s',
                width: isMobile ? '100%' : 'auto'
              }}
              onMouseOver={e => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
              onMouseOut={e => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'}
            >
              <span>Ver Demo del Panel</span>
              <ChevronDown size={18} />
            </a>
          </div>

          {/* Micro-copy de Seguridad B2B */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: isMobile ? '0.75rem' : '1.5rem',
            flexWrap: 'wrap',
            fontSize: '0.8rem',
            color: 'rgba(255, 255, 255, 0.5)'
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Check size={14} color="#10b981" /> 14 días sin tarjeta de crédito
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Check size={14} color="#10b981" /> Tus atletas solo necesitan Strava
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Check size={14} color="#10b981" /> Configuración en 2 minutos
            </span>
          </div>

          {/* MOCKUP VISUAL DEL PANEL DE ENTRENADOR */}
          <div style={{
            marginTop: '3.5rem',
            position: 'relative',
            width: '100%',
            maxWidth: '1020px',
            margin: '3.5rem auto 0'
          }}>
            {/* Ambient Glow */}
            <div style={{
              position: 'absolute',
              top: '20%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '85%',
              height: '70%',
              background: 'radial-gradient(circle, rgba(139, 92, 246, 0.25) 0%, rgba(217, 70, 239, 0.12) 50%, transparent 75%)',
              filter: 'blur(70px)',
              zIndex: 0,
              pointerEvents: 'none'
            }} />

            {/* Window Frame */}
            <div style={{
              position: 'relative',
              zIndex: 1,
              background: 'rgba(15, 20, 35, 0.96)',
              border: '1px solid rgba(139, 92, 246, 0.35)',
              borderRadius: '20px',
              overflow: 'hidden',
              boxShadow: '0 30px 80px rgba(0, 0, 0, 0.85), 0 0 50px rgba(139, 92, 246, 0.2)'
            }}>
              {/* Browser / App Header Bar */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0.85rem 1.25rem',
                background: 'rgba(10, 14, 26, 0.95)',
                borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '11px', height: '11px', borderRadius: '50%', background: '#ef4444' }} />
                  <div style={{ width: '11px', height: '11px', borderRadius: '50%', background: '#f59e0b' }} />
                  <div style={{ width: '11px', height: '11px', borderRadius: '50%', background: '#10b981' }} />
                  <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.4)', marginLeft: '12px', fontWeight: 600 }}>
                    nousmentor.app / panel-entrenador
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{
                    fontSize: '0.72rem',
                    background: 'rgba(16, 185, 129, 0.15)',
                    color: '#6ee7b7',
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                    padding: '3px 10px',
                    borderRadius: '12px',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}>
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#10b981', display: 'inline-block' }} />
                    Sincronización Activa (Strava & Garmin)
                  </span>
                </div>
              </div>

              {/* Imagen Real del Dashboard */}
              <div style={{ position: 'relative', overflow: 'hidden' }}>
                <img
                  src={mentorDashboardMockup}
                  alt="Panel Centralizado de Atletas NousMentor - Visualización de Carga TSB, Sueño, HRV y Alertas"
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                    filter: 'contrast(1.02)'
                  }}
                  loading="eager"
                />

                {/* Badges Flotantes sobre la Imagen (Desktop) */}
                {!isMobile && (
                  <>
                    <div style={{
                      position: 'absolute',
                      top: '18px',
                      right: '18px',
                      background: 'rgba(15, 20, 35, 0.88)',
                      backdropFilter: 'blur(16px)',
                      border: '1px solid rgba(239, 68, 68, 0.45)',
                      padding: '10px 16px',
                      borderRadius: '14px',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#ef4444', boxShadow: '0 0 10px #ef4444' }} />
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#fca5a5' }}>Alerta Temprana de Sobrecarga</div>
                        <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)' }}>3 atletas requieren ajuste antes de entrenar</div>
                      </div>
                    </div>

                    <div style={{
                      position: 'absolute',
                      bottom: '20px',
                      left: '20px',
                      background: 'rgba(15, 20, 35, 0.9)',
                      backdropFilter: 'blur(16px)',
                      border: '1px solid rgba(139, 92, 246, 0.45)',
                      padding: '10px 18px',
                      borderRadius: '14px',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <Sparkles size={18} color="#d946ef" />
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#e9d5ff' }}>Copiloto Biológico IA</div>
                        <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)' }}>Desacopla capacidad fisiológica y dosis recomendada</div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Barra inferior de estado rápido */}
              <div style={{
                background: 'rgba(12, 17, 30, 0.95)',
                borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                padding: '1rem 1.5rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '1rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.7)' }}>
                    Vista en vivo: <strong style={{ color: '#fff' }}>24 atletas monitorizados</strong>
                  </span>
                  <span style={{ fontSize: '0.75rem', background: 'rgba(239,68,68,0.15)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.3)', padding: '3px 8px', borderRadius: '10px', fontWeight: 700 }}>
                    🚨 3 en Alerta
                  </span>
                  <span style={{ fontSize: '0.75rem', background: 'rgba(16,185,129,0.15)', color: '#6ee7b7', border: '1px solid rgba(16,185,129,0.3)', padding: '3px 8px', borderRadius: '10px', fontWeight: 700 }}>
                    🟢 18 Listos
                  </span>
                </div>

                <button
                  onClick={() => navigate('/app?mode=register&role=trainer')}
                  style={{
                    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.3) 0%, rgba(217, 70, 239, 0.3) 100%)',
                    border: '1px solid rgba(139, 92, 246, 0.5)',
                    color: '#e9d5ff',
                    padding: '0.55rem 1.1rem',
                    borderRadius: '25px',
                    fontSize: '0.82rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={e => e.currentTarget.style.background = 'linear-gradient(135deg, rgba(139, 92, 246, 0.45) 0%, rgba(217, 70, 239, 0.45) 100%)'}
                  onMouseOut={e => e.currentTarget.style.background = 'linear-gradient(135deg, rgba(139, 92, 246, 0.3) 0%, rgba(217, 70, 239, 0.3) 100%)'}
                >
                  <Sparkles size={14} color="#d946ef" />
                  <span>Explorar Demostración Interactiva</span>
                  <ArrowRight size={14} />
                </button>
              </div>

            </div>
          </div>

        </div>
      </section>

      {/* ═══════════════════════════════════════════════ */}
      {/* 3. BARRA DE INTEGRACIONES PARA ENTRENADORES     */}
      {/* ═══════════════════════════════════════════════ */}
      <section style={{
        padding: '2.5rem 5%',
        background: 'rgba(255, 255, 255, 0.02)',
        borderTop: '1px solid rgba(255, 255, 255, 0.05)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        textAlign: 'center'
      }}>
        <p style={{
          fontSize: '0.78rem',
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'rgba(255, 255, 255, 0.4)',
          marginBottom: '1.25rem'
        }}>
          Compatible con los ecosistemas de tus atletas (Sin cambiar de reloj ni de hábitos)
        </p>

        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: isMobile ? '1rem' : '2.5rem',
          opacity: 0.85
        }}>
          {['Strava Partner', 'Garmin Connect', 'TrainingPeaks Sync', 'Wahoo Fitness', 'Polar Flow', 'Apple Health', 'Coros Training'].map((brand, idx) => (
            <div
              key={idx}
              style={{
                fontSize: isMobile ? '0.9rem' : '1.05rem',
                fontWeight: 700,
                color: 'rgba(255, 255, 255, 0.75)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <span style={{ color: '#8b5cf6', opacity: 0.6 }}>•</span> {brand}
            </div>
          ))}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════ */}
      {/* 4. EL CUELLO DE BOTELLA DEL ENTRENADOR          */}
      {/* ═══════════════════════════════════════════════ */}
      <section style={{ padding: isMobile ? '4.5rem 6%' : '7rem 5%', background: '#070a14' }}>
        <div style={{ maxWidth: '960px', margin: '0 auto', textAlign: 'center' }}>
          
          <span style={{ color: '#d946ef', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            El Dilema del Coach
          </span>
          <h2 style={{
            fontSize: isMobile ? 'clamp(2rem, 7vw, 2.5rem)' : 'clamp(2.4rem, 4.5vw, 3.4rem)',
            fontWeight: 800,
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
            margin: '0.75rem 0 1.5rem'
          }}>
            Quieres escalar tu negocio.<br />
            <span style={{ color: 'rgba(255,255,255,0.5)' }}>Pero el día solo tiene 24 horas.</span>
          </h2>
          <p style={{ fontSize: '1.15rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, maxWidth: '750px', margin: '0 auto 3.5rem' }}>
            Muchos entrenadores tocan un techo de 15 a 20 pupilos no por falta de clientes, sino porque el análisis manual de datos y la redacción de informes les consume los fines de semana.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
            gap: '1.25rem',
            textAlign: 'left'
          }}>
            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '1.75rem' }}>
              <div style={{ color: '#ef4444', fontSize: '1.8rem', marginBottom: '0.5rem' }}>⏰</div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.5rem' }}>Fatiga de Pantalla</h3>
              <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.5, margin: 0 }}>
                Abrir 20 ficheros FIT a mano los domingos por la tarde para escribir resúmenes que una IA puede redactar en 3 segundos.
              </p>
            </div>

            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '1.75rem' }}>
              <div style={{ color: '#f59e0b', fontSize: '1.8rem', marginBottom: '0.5rem' }}>🩹</div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.5rem' }}>Lesiones Silenciosas</h3>
              <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.5, margin: 0 }}>
                Un atleta sale a hacer series con 4h de sueño y caída de HRV. No te avisa y se rompe. Pierdes un cliente por falta de datos en tiempo real.
              </p>
            </div>

            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '1.75rem' }}>
              <div style={{ color: '#8b5cf6', fontSize: '1.8rem', marginBottom: '0.5rem' }}>📉</div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.5rem' }}>Tope de Facturación</h3>
              <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.5, margin: 0 }}>
                Para ganar más dinero necesitas más atletas, pero más atletas significa menos calidad de vida. Con NousMentor rompes ese cuello de botella.
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* ═══════════════════════════════════════════════ */}
      {/* 5. CÓMO NOUSMENTOR TE HACE GANAR TIEMPO Y DINERO */}
      {/* ═══════════════════════════════════════════════ */}
      <section id="panel" style={{ padding: isMobile ? '4.5rem 6%' : '7rem 5%', background: '#050810', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <span style={{ color: '#8b5cf6', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Solución de Alta Eficiencia
            </span>
            <h2 style={{
              fontSize: isMobile ? 'clamp(2rem, 7vw, 2.5rem)' : 'clamp(2.4rem, 4.5vw, 3.4rem)',
              fontWeight: 800,
              lineHeight: 1.15,
              letterSpacing: '-0.02em',
              margin: '0.75rem 0 1rem'
            }}>
              Tu copiloto clínico de entrenamiento.
            </h2>
            <p style={{ fontSize: '1.15rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, maxWidth: '750px', margin: '0 auto' }}>
              NousMentor no viene a decirte qué entrenamientos mandar; viene a darte la certeza biológica de que tus pupilos están asimilando la carga sin romperse.
            </p>
          </div>

          {/* SPOTLIGHT VISUAL: ENTRENADOR EN ACCIÓN A PIE DE PISTA */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1.1fr 0.9fr',
            gap: isMobile ? '2rem' : '3rem',
            alignItems: 'center',
            background: 'linear-gradient(135deg, rgba(20, 26, 48, 0.7) 0%, rgba(12, 16, 30, 0.9) 100%)',
            border: '1px solid rgba(139, 92, 246, 0.25)',
            borderRadius: '24px',
            padding: isMobile ? '1.5rem' : '2.5rem',
            marginBottom: '3rem',
            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.5)'
          }}>
            {/* Imagen Fotográfica del Entrenador */}
            <div style={{ position: 'relative', borderRadius: '18px', overflow: 'hidden', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <img
                src={mentorActionTrack}
                alt="Entrenador analizando telemetría y fisiología deportiva en tablet a pie de pista"
                style={{
                  width: '100%',
                  height: '100%',
                  maxHeight: '380px',
                  objectFit: 'cover',
                  display: 'block'
                }}
                loading="lazy"
              />
              <div style={{
                position: 'absolute',
                bottom: '12px',
                left: '12px',
                right: '12px',
                background: 'rgba(10, 14, 26, 0.88)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: '12px',
                padding: '10px 14px',
                fontSize: '0.78rem',
                color: 'rgba(255, 255, 255, 0.85)'
              }}>
                <strong style={{ color: '#c4b5fd' }}>Telemetría en tiempo real:</strong> Accede al estado de tu equipo en cualquier lugar, desde tu tablet o smartphone.
              </div>
            </div>

            {/* Contenido Editorial */}
            <div>
              <div style={{ display: 'inline-flex', padding: '5px 12px', background: 'rgba(16, 185, 129, 0.15)', border: '1px solid rgba(16, 185, 129, 0.3)', borderRadius: '16px', color: '#6ee7b7', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: '1rem' }}>
                Diseñado para el Mundo Real
              </div>
              <h3 style={{ fontSize: isMobile ? '1.5rem' : '1.9rem', fontWeight: 800, color: 'white', lineHeight: 1.2, marginBottom: '1rem' }}>
                A pie de pista, en el rodillo o tomando café.
              </h3>
              <p style={{ fontSize: '0.98rem', color: 'rgba(255, 255, 255, 0.72)', lineHeight: 1.6, marginBottom: '1.5rem' }}>
                La mayoría de plataformas de entrenamiento exigen horas frente al ordenador para interpretar gráficas crípticas. NousMentor condensa la fisiología compleja en un <strong style={{ color: '#fff' }}>diagnóstico accionable</strong> que puedes consultar antes de empezar la sesión.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(139, 92, 246, 0.2)', border: '1px solid #8b5cf6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>
                    <Check size={13} color="#c4b5fd" />
                  </div>
                  <div style={{ fontSize: '0.88rem', color: 'rgba(255, 255, 255, 0.85)' }}>
                    <strong style={{ color: '#fff' }}>Umbrales Ventilatorios Reales:</strong> Integra las zonas VT1, VT2 y VO2Max de las pruebas de esfuerzo médicas de tus atletas.
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(139, 92, 246, 0.2)', border: '1px solid #8b5cf6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>
                    <Check size={13} color="#c4b5fd" />
                  </div>
                  <div style={{ fontSize: '0.88rem', color: 'rgba(255, 255, 255, 0.85)' }}>
                    <strong style={{ color: '#fff' }}>Veto Preventivo de Seguridad:</strong> Si el sueño o el HRV de un pupilo colapsan, el sistema te recomienda suspender series lácticas antes de una rotura.
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(139, 92, 246, 0.2)', border: '1px solid #8b5cf6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>
                    <Check size={13} color="#c4b5fd" />
                  </div>
                  <div style={{ fontSize: '0.88rem', color: 'rgba(255, 255, 255, 0.85)' }}>
                    <strong style={{ color: '#fff' }}>Informes IA en 1 Clic:</strong> Envía un resumen profesional de evolución semanal a tus atletas o a la directiva del club sin perder tu domingo.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '2rem'
          }}>
            {/* Función 1: Semáforo Biológico */}
            <div style={{
              background: 'radial-gradient(ellipse at top left, rgba(139, 92, 246, 0.12) 0%, rgba(15, 20, 35, 0.8) 100%)',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              borderRadius: '24px',
              padding: isMobile ? '2rem 1.5rem' : '2.5rem',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              gap: '1.5rem'
            }}>
              <div>
                <div style={{ display: 'inline-flex', padding: '6px 14px', background: 'rgba(139, 92, 246, 0.2)', borderRadius: '20px', color: '#c4b5fd', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: '1rem' }}>
                  A las 08:00 AM
                </div>
                <h3 style={{ fontSize: '1.6rem', fontWeight: 800, color: 'white', marginBottom: '0.75rem' }}>
                  Semáforo Biológico Matutino
                </h3>
                <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
                  Abre tu panel matutino y ve al instante el estado de todo tu grupo. Si un pupilo tiene caída de HRV y TSB negativo alto, el sistema te marca una alerta roja para que le escribas antes de que salga de casa.
                </p>
              </div>

              <div style={{ background: 'rgba(0,0,0,0.4)', borderRadius: '12px', padding: '1rem', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ fontSize: '0.75rem', color: '#10b981', fontWeight: 700 }}>Beneficio Real:</div>
                <div style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.8)', marginTop: '4px' }}>
                  Revisas a 25 atletas en solo 10 minutos de café y previenes lesiones por sorpresa.
                </div>
              </div>
            </div>

            {/* Función 2: Cero Apps Raras */}
            <div style={{
              background: 'radial-gradient(ellipse at top left, rgba(217, 70, 239, 0.1) 0%, rgba(15, 20, 35, 0.8) 100%)',
              border: '1px solid rgba(217, 70, 239, 0.3)',
              borderRadius: '24px',
              padding: isMobile ? '2rem 1.5rem' : '2.5rem',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              gap: '1.5rem'
            }}>
              <div>
                <div style={{ display: 'inline-flex', padding: '6px 14px', background: 'rgba(217, 70, 239, 0.2)', borderRadius: '20px', color: '#f5d0fe', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: '1rem' }}>
                  Cero Fricción
                </div>
                <h3 style={{ fontSize: '1.6rem', fontWeight: 800, color: 'white', marginBottom: '0.75rem' }}>
                  Tus Atletas Solo Usan Strava
                </h3>
                <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
                  No tienes que pedirles que se instalen otra aplicación ni que aprendan interfaces difíciles. Les envías tu enlace único de invitación, autorizan Strava en 30 segundos y sus datos vuelan a tu panel.
                </p>
              </div>

              <div style={{ background: 'rgba(0,0,0,0.4)', borderRadius: '12px', padding: '1rem', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ fontSize: '0.75rem', color: '#d946ef', fontWeight: 700 }}>Tasa de Adopción del 100%:</div>
                <div style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.8)', marginTop: '4px' }}>
                  Tus atletas siguen entrenando con su reloj de siempre. Ninguna queja técnica.
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ═══════════════════════════════════════════════ */}
      {/* 6. CALCULADORA INTERACTIVA DE RETORNO (ROI)     */}
      {/* ═══════════════════════════════════════════════ */}
      <section id="roi" style={{ padding: isMobile ? '4.5rem 6%' : '7rem 5%', background: '#070a14', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div style={{ maxWidth: '850px', margin: '0 auto', textAlign: 'center' }}>
          
          <span style={{ color: '#10b981', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Retorno de Inversión Inmediato
          </span>
          <h2 style={{
            fontSize: isMobile ? 'clamp(2rem, 7vw, 2.5rem)' : 'clamp(2.4rem, 4.5vw, 3.4rem)',
            fontWeight: 800,
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
            margin: '0.75rem 0 1rem'
          }}>
            Calcula cuánto ganas con NousMentor
          </h2>
          <p style={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.65)', marginBottom: '3rem' }}>
            NousMentor no es un coste para tu negocio; es un servicio premium que te permite incrementar tu tarifa mensual por atleta.
          </p>

          <div style={{
            background: 'rgba(15, 20, 35, 0.9)',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '24px',
            padding: isMobile ? '2rem 1.5rem' : '3rem',
            textAlign: 'left'
          }}>
            {/* Control Deslizante: Atletas */}
            <div style={{ marginBottom: '2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <label style={{ fontSize: '1rem', fontWeight: 700, color: 'white' }}>Número de atletas que entrenas:</label>
                <span style={{ fontSize: '1.5rem', fontWeight: 900, color: '#8b5cf6' }}>{athletesCount} pupilos</span>
              </div>
              <input
                type="range"
                min="5"
                max="50"
                value={athletesCount}
                onChange={e => setAthletesCount(Number(e.target.value))}
                style={{ width: '100%', accentColor: '#8b5cf6', cursor: 'pointer' }}
              />
            </div>

            {/* Control Deslizante: Plus por servicio biométrico */}
            <div style={{ marginBottom: '2.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <label style={{ fontSize: '1rem', fontWeight: 700, color: 'white' }}>Tarifa extra mensual por atleta (Seguimiento Biométrico IA):</label>
                <span style={{ fontSize: '1.5rem', fontWeight: 900, color: '#d946ef' }}>+{extraFee} € / mes</span>
              </div>
              <input
                type="range"
                min="5"
                max="25"
                step="5"
                value={extraFee}
                onChange={e => setExtraFee(Number(e.target.value))}
                style={{ width: '100%', accentColor: '#d946ef', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.45)', marginTop: '4px', display: 'block' }}>
                *La mayoría de entrenadores cobran entre 10 € y 20 € extra al mes por el servicio con informes semanales automáticos.
              </span>
            </div>

            {/* Resultado del Cálculo */}
            <div style={{
              background: 'radial-gradient(ellipse at top left, rgba(16, 185, 129, 0.12) 0%, rgba(10, 15, 25, 0.95) 100%)',
              border: '1px solid rgba(16, 185, 129, 0.35)',
              borderRadius: '16px',
              padding: '1.5rem',
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
              gap: '1rem',
              alignItems: 'center',
              textAlign: 'center'
            }}>
              <div>
                <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase' }}>Ingresos Extra</div>
                <div style={{ fontSize: '1.8rem', fontWeight: 900, color: '#10b981', marginTop: '4px' }}>+{monthlyExtraRevenue} €<span style={{ fontSize: '0.85rem' }}>/mes</span></div>
              </div>

              <div>
                <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase' }}>Coste NousMentor</div>
                <div style={{ fontSize: '1.8rem', fontWeight: 900, color: 'rgba(255,255,255,0.8)', marginTop: '4px' }}>29 €<span style={{ fontSize: '0.85rem' }}>/mes</span></div>
              </div>

              <div>
                <div style={{ fontSize: '0.75rem', color: '#c4b5fd', textTransform: 'uppercase', fontWeight: 800 }}>Beneficio Neto Extra</div>
                <div style={{ fontSize: '2.2rem', fontWeight: 900, color: '#d946ef', marginTop: '4px' }}>+{netMonthlyProfit} €<span style={{ fontSize: '0.85rem' }}>/mes</span></div>
              </div>
            </div>

            <div style={{ marginTop: '2rem', textAlign: 'center' }}>
              <button
                onClick={() => navigate('/app?mode=register&role=trainer')}
                style={{
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #d946ef 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '1rem 2rem',
                  borderRadius: '30px',
                  fontWeight: 800,
                  fontSize: '1rem',
                  cursor: 'pointer',
                  boxShadow: '0 8px 25px rgba(139, 92, 246, 0.4)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span>Probar Piloto con 3 Atletas Gratis</span>
                <ArrowRight size={18} />
              </button>
            </div>

          </div>

        </div>
      </section>

      {/* ═══════════════════════════════════════════════ */}
      {/* 7. PRECIOS B2B CLAROS Y REPARACIÓN CLUB 99€     */}
      {/* ═══════════════════════════════════════════════ */}
      <section id="pricing" style={{ padding: isMobile ? '4.5rem 6%' : '7rem 5%', background: '#050810', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div style={{ maxWidth: '960px', margin: '0 auto', textAlign: 'center' }}>
          
          <span style={{ color: '#8b5cf6', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Planes B2B Transparentes
          </span>
          <h2 style={{
            fontSize: isMobile ? 'clamp(2rem, 7vw, 2.5rem)' : 'clamp(2.4rem, 4.5vw, 3.4rem)',
            fontWeight: 800,
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
            margin: '0.75rem 0 1rem'
          }}>
            Escala tu negocio de entrenamiento
          </h2>
          <p style={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.65)', marginBottom: '3.5rem' }}>
            Comienza con una prueba piloto gratuita de 14 días. Sin permanencias.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '2rem',
            textAlign: 'left'
          }}>
            {/* Plan Entrenador Pro (29 €) */}
            <div style={{
              background: 'radial-gradient(ellipse at top left, rgba(139, 92, 246, 0.12) 0%, rgba(15, 20, 35, 0.95) 100%)',
              border: '2px solid #8b5cf6',
              borderRadius: '24px',
              padding: isMobile ? '2rem 1.5rem' : '3rem 2.5rem',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              position: 'relative',
              boxShadow: '0 20px 50px rgba(139, 92, 246, 0.2)'
            }}>
              <div style={{
                position: 'absolute',
                top: '-14px',
                right: '25px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #d946ef 100%)',
                color: '#fff',
                padding: '4px 14px',
                borderRadius: '20px',
                fontSize: '0.75rem',
                fontWeight: 900,
                letterSpacing: '0.04em'
              }}>
                14 DÍAS PILOTO GRATIS
              </div>

              <div>
                <h3 style={{ fontSize: '1.6rem', fontWeight: 800, marginBottom: '0.25rem', color: '#c4b5fd' }}>Entrenador Pro</h3>
                <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)', marginBottom: '1.5rem' }}>
                  Para preparadores físicos independientes con atletas de resistencia.
                </p>
                <div style={{ fontSize: '3.2rem', fontWeight: 900, marginBottom: '1rem' }}>
                  29 € <span style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>/mes</span>
                </div>
                <div style={{ fontSize: '0.8rem', color: '#10b981', fontWeight: 700, marginBottom: '1.5rem', background: 'rgba(16,185,129,0.1)', padding: '6px 10px', borderRadius: '8px', display: 'inline-block' }}>
                  ⭐ Bonificación 100%: si tus atletas usan Pro, tu cuota baja a 0 €
                </div>

                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.9rem', fontSize: '0.9rem', color: 'rgba(255,255,255,0.85)' }}>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={16} color="#8b5cf6" /> <strong>Atletas ilimitados</strong></li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={16} color="#8b5cf6" /> Semáforo biológico diario matutino</li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={16} color="#8b5cf6" /> Alertas predictivas de fatiga sistémica y TSB</li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={16} color="#8b5cf6" /> Generador de informes semanales IA en 1 clic</li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={16} color="#8b5cf6" /> Sincronización transparente vía Strava</li>
                </ul>
              </div>

              <button
                onClick={() => navigate('/app?mode=register&role=trainer')}
                style={{
                  marginTop: '2.5rem',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #d946ef 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '1rem',
                  borderRadius: '14px',
                  fontWeight: 800,
                  fontSize: '1rem',
                  cursor: 'pointer',
                  width: '100%',
                  boxShadow: '0 8px 25px rgba(139, 92, 246, 0.4)',
                  transition: 'transform 0.2s'
                }}
                onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
              >
                Empezar Piloto con 3 Atletas
              </button>
            </div>

            {/* Plan Elite Squad / Clubes (99 €) con Modal VIP */}
            <div style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '24px',
              padding: isMobile ? '2rem 1.5rem' : '3rem 2.5rem',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}>
              <div>
                <h3 style={{ fontSize: '1.6rem', fontWeight: 800, marginBottom: '0.25rem', color: 'white' }}>Elite Squad / Clubes</h3>
                <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)', marginBottom: '1.5rem' }}>
                  Para clubes deportivos, academias de ciclismo/running y múltiples entrenadores.
                </p>
                <div style={{ fontSize: '3.2rem', fontWeight: 900, marginBottom: '1.5rem' }}>
                  99 € <span style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>/mes</span>
                </div>

                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.9rem', fontSize: '0.9rem', color: 'rgba(255,255,255,0.75)' }}>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={16} color="#d946ef" /> <strong>Todo lo de Entrenador Pro</strong></li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={16} color="#d946ef" /> <strong>Marca Blanca: Tu logotipo del club en los informes</strong></li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={16} color="#d946ef" /> Panel multi-entrenador para coordinadores</li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={16} color="#d946ef" /> Onboarding asistido por videollamada para tu staff</li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={16} color="#d946ef" /> Facturación corporativa única con NIF/CIF</li>
                </ul>
              </div>

              <button
                onClick={() => setIsDemoModalOpen(true)}
                style={{
                  marginTop: '2.5rem',
                  background: 'rgba(255,255,255,0.06)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.2)',
                  padding: '1rem',
                  borderRadius: '14px',
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  cursor: 'pointer',
                  width: '100%',
                  transition: 'background 0.2s'
                }}
                onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
                onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
              >
                Solicitar Demostración para Clubes
              </button>
            </div>
          </div>

        </div>
      </section>

      {/* ═══════════════════════════════════════════════ */}
      {/* 8. FAQ B2B PARA ENTRENADORES                   */}
      {/* ═══════════════════════════════════════════════ */}
      <section id="faq" style={{ padding: isMobile ? '4.5rem 6%' : '7rem 5%', background: '#070a14' }}>
        <div style={{ maxWidth: '850px', margin: '0 auto' }}>
          
          <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
            <span style={{ color: '#8b5cf6', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Preguntas de Negocio
            </span>
            <h2 style={{ fontSize: isMobile ? '1.8rem' : '2.5rem', fontWeight: 800, margin: '0.5rem 0' }}>
              Preguntas Frecuentes del Entrenador
            </h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            
            <details className="mentor-faq-item">
              <summary style={{ padding: '1.25rem 1.5rem', background: 'rgba(139, 92, 246, 0.04)', border: '1px solid rgba(139, 92, 246, 0.15)', borderRadius: '12px', fontSize: '1.05rem', fontWeight: 600, cursor: 'pointer', listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                ¿Puedo seguir usando TrainingPeaks junto con NousMentor?
                <ChevronDown className="mentor-faq-icon" size={20} color="#8b5cf6" />
              </summary>
              <div style={{ padding: '1.25rem 1.5rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.7, fontSize: '0.95rem' }}>
                Completamente. NousMentor no viene a sustituir tu biblioteca de entrenamientos estructurados; es la <strong>capa de inteligencia fisiológica</strong> que TrainingPeaks no ofrece. Mientras planificas tus semanas como siempre, NousMentor te avisa a las 8:00 AM si un atleta tiene riesgo de sobreentrenamiento o si su sueño ha dejado de asimilar las cargas.
              </div>
            </details>

            <details className="mentor-faq-item">
              <summary style={{ padding: '1.25rem 1.5rem', background: 'rgba(139, 92, 246, 0.04)', border: '1px solid rgba(139, 92, 246, 0.15)', borderRadius: '12px', fontSize: '1.05rem', fontWeight: 600, cursor: 'pointer', listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                ¿Tienen que pagar mis atletas una suscripción obligatoria?
                <ChevronDown className="mentor-faq-icon" size={20} color="#8b5cf6" />
              </summary>
              <div style={{ padding: '1.25rem 1.5rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.7, fontSize: '0.95rem' }}>
                No. Tus atletas solo necesitan autorizar la conexión con Strava y usarán el plan Basic gratuito para enviarte sus datos. Si algún atleta decide por su cuenta suscribirse al plan Atleta Pro, nuestro sistema de referidos te aplicará bonificaciones automáticas en tu cuota de 29 €, pudiendo salirte la plataforma 100% gratis.
              </div>
            </details>

            <details className="mentor-faq-item">
              <summary style={{ padding: '1.25rem 1.5rem', background: 'rgba(139, 92, 246, 0.04)', border: '1px solid rgba(139, 92, 246, 0.15)', borderRadius: '12px', fontSize: '1.05rem', fontWeight: 600, cursor: 'pointer', listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                ¿Cómo funciona la Marca Blanca en el plan Elite Squad?
                <ChevronDown className="mentor-faq-icon" size={20} color="#8b5cf6" />
              </summary>
              <div style={{ padding: '1.25rem 1.5rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.7, fontSize: '0.95rem' }}>
                En el plan Elite Squad (99 €/mes) personalizamos los informes semanales en PDF y el entorno de tus pupilos con el logotipo y colores corporativos de tu club o academia. Tus atletas perciben un servicio tecnológico exclusivo desarrollado por tu marca.
              </div>
            </details>

            <details className="mentor-faq-item">
              <summary style={{ padding: '1.25rem 1.5rem', background: 'rgba(139, 92, 246, 0.04)', border: '1px solid rgba(139, 92, 246, 0.15)', borderRadius: '12px', fontSize: '1.05rem', fontWeight: 600, cursor: 'pointer', listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                ¿De verdad los atletas son ilimitados en el plan de 29 €?
                <ChevronDown className="mentor-faq-icon" size={20} color="#8b5cf6" />
              </summary>
              <div style={{ padding: '1.25rem 1.5rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.7, fontSize: '0.95rem' }}>
                Sí. No te penalizamos por crecer. Ya tengas 10, 30 o 50 deportistas a tu cargo, tu cuota mensual se mantiene fija en 29 €/mes.
              </div>
            </details>

          </div>

        </div>
      </section>

      {/* ═══════════════════════════════════════════════ */}
      {/* 9. CTA FINAL B2B DE ALTA CONVERSIÓN            */}
      {/* ═══════════════════════════════════════════════ */}
      <section style={{
        padding: isMobile ? '5rem 6%' : '7rem 5%',
        background: 'radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.15) 0%, #050810 70%)',
        textAlign: 'center',
        borderTop: '1px solid rgba(255,255,255,0.05)'
      }}>
        <div style={{ maxWidth: '650px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: isMobile ? 'clamp(2.2rem, 7vw, 2.7rem)' : 'clamp(2.8rem, 4.5vw, 3.6rem)',
            fontWeight: 900,
            letterSpacing: '-0.03em',
            margin: '0 0 1.25rem 0',
            lineHeight: 1.15
          }}>
            Deja de perder horas.<br />
            <span style={{ color: '#c4b5fd' }}>Entrena como una academia de élite.</span>
          </h2>
          <p style={{ fontSize: '1.15rem', color: 'rgba(255,255,255,0.7)', marginBottom: '2.5rem', lineHeight: 1.6 }}>
            Únete a los preparadores físicos que ya controlan la fatiga de decenas de deportistas sin quemarse los domingos.
          </p>

          <button
            onClick={() => navigate('/app?mode=register&role=trainer')}
            style={{
              background: 'linear-gradient(135deg, #8b5cf6 0%, #d946ef 100%)',
              color: '#fff',
              border: 'none',
              padding: isMobile ? '1.1rem 2rem' : '1.3rem 2.8rem',
              borderRadius: '35px',
              fontWeight: 900,
              fontSize: isMobile ? '1.1rem' : '1.25rem',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              boxShadow: '0 10px 35px rgba(139, 92, 246, 0.45)',
              transition: 'transform 0.2s',
              width: isMobile ? '100%' : 'auto',
              justifyContent: 'center'
            }}
            onMouseOver={e => e.currentTarget.style.transform = 'translateY(-3px)'}
            onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <span>Iniciar Piloto Gratuito con 3 Atletas</span>
            <ArrowRight size={22} />
          </button>
          
          <div style={{ marginTop: '1rem', fontSize: '0.8rem', color: 'rgba(255,255,255,0.45)' }}>
            14 días de prueba completa · Sin tarjeta de crédito · Tus atletas solo usan Strava
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <GlobalFooter />

      {/* ═══════════════════════════════════════════════ */}
      {/* MODAL VIP PARA CLUBES (REPARACIÓN EMBUTO 99€)   */}
      {/* ═══════════════════════════════════════════════ */}
      {isDemoModalOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          backdropFilter: 'blur(10px)',
          zIndex: 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1.25rem'
        }}>
          <div style={{
            background: '#0c101c',
            border: '1px solid rgba(139, 92, 246, 0.4)',
            borderRadius: '24px',
            maxWidth: '520px',
            width: '100%',
            padding: isMobile ? '1.75rem' : '2.5rem',
            position: 'relative',
            boxShadow: '0 25px 60px rgba(0, 0, 0, 0.9), 0 0 50px rgba(139, 92, 246, 0.2)'
          }}>
            <button
              onClick={() => { setIsDemoModalOpen(false); setDemoSubmitted(false); }}
              style={{ position: 'absolute', top: '20px', right: '20px', background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer' }}
            >
              <X size={20} />
            </button>

            {!demoSubmitted ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#c4b5fd', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                  <Building size={16} /> Demostración para Clubes y Academias
                </div>
                <h3 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'white', margin: '0 0 0.75rem 0' }}>
                  Solicita una Demo VIP
                </h3>
                <p style={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.5, marginBottom: '1.5rem' }}>
                  Descubre cómo el plan Elite Squad (99 €/mes) implementa Marca Blanca para tu club y conecta a múltiples entrenadores.
                </p>

                <form onSubmit={handleDemoSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div>
                    <label style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Tu Nombre</label>
                    <input
                      type="text"
                      required
                      placeholder="Ej. Carlos Martínez"
                      value={demoForm.name}
                      onChange={e => setDemoForm({ ...demoForm, name: e.target.value })}
                      style={{ width: '100%', padding: '0.75rem 1rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '10px', color: 'white', fontSize: '0.9rem' }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Email Corporativo o Personal</label>
                    <input
                      type="email"
                      required
                      placeholder="carlos@clubciclismo.es"
                      value={demoForm.email}
                      onChange={e => setDemoForm({ ...demoForm, email: e.target.value })}
                      style={{ width: '100%', padding: '0.75rem 1rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '10px', color: 'white', fontSize: '0.9rem' }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    <div>
                      <label style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Nombre del Club / Equipo</label>
                      <input
                        type="text"
                        required
                        placeholder="Ej. Triteam Madrid"
                        value={demoForm.clubName}
                        onChange={e => setDemoForm({ ...demoForm, clubName: e.target.value })}
                        style={{ width: '100%', padding: '0.75rem 1rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '10px', color: 'white', fontSize: '0.9rem' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Nº de Atletas Estimado</label>
                      <select
                        value={demoForm.athletesNum}
                        onChange={e => setDemoForm({ ...demoForm, athletesNum: e.target.value })}
                        style={{ width: '100%', padding: '0.75rem 1rem', background: '#13192b', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '10px', color: 'white', fontSize: '0.9rem' }}
                      >
                        <option value="10-25">10 - 25 atletas</option>
                        <option value="25-50">25 - 50 atletas</option>
                        <option value="50-100">50 - 100 atletas</option>
                        <option value="100+">Más de 100 atletas</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Teléfono o WhatsApp (Opcional)</label>
                    <input
                      type="tel"
                      placeholder="+34 600 000 000"
                      value={demoForm.phone}
                      onChange={e => setDemoForm({ ...demoForm, phone: e.target.value })}
                      style={{ width: '100%', padding: '0.75rem 1rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '10px', color: 'white', fontSize: '0.9rem' }}
                    />
                  </div>

                  <button
                    type="submit"
                    style={{
                      marginTop: '0.5rem',
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #d946ef 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '1rem',
                      borderRadius: '12px',
                      fontWeight: 800,
                      fontSize: '1rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    <span>Solicitar Demostración</span>
                    <Send size={16} />
                  </button>
                </form>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
                <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: 'rgba(16,185,129,0.15)', color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
                  <Check size={32} />
                </div>
                <h3 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'white', marginBottom: '0.5rem' }}>
                  ¡Solicitud Recibida con Éxito!
                </h3>
                <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, marginBottom: '2rem' }}>
                  Gracias, {demoForm.name}. Nuestro equipo de producto te contactará en menos de 2 horas para coordinar la demo personalizada para <strong>{demoForm.clubName}</strong>.
                </p>
                <button
                  onClick={() => setIsDemoModalOpen(false)}
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    color: 'white',
                    border: '1px solid rgba(255,255,255,0.2)',
                    padding: '0.75rem 2rem',
                    borderRadius: '20px',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  Cerrar
                </button>
              </div>
            )}

          </div>
        </div>
      )}

      {/* ESTILOS DE INTERACCIÓN */}
      <style>{`
        .mentor-nav-link {
          color: rgba(255, 255, 255, 0.7);
          text-decoration: none;
          transition: color 0.2s;
        }
        .mentor-nav-link:hover {
          color: #c4b5fd;
        }
        .mentor-faq-item summary::-webkit-details-marker { display: none; }
        .mentor-faq-item[open] .mentor-faq-icon { transform: rotate(180deg); }
        .mentor-faq-icon { transition: transform 0.3s; }
      `}</style>
    </div>
  );
}
